import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor, HttpRequest, HttpErrorResponse
} from '@angular/common/http';
import {
  defer,
  from,
  iif,
  Observable, of,
  throwError,
  timer
} from 'rxjs';

import { Inject, Injectable, Optional } from '@angular/core';
import { Router } from '@angular/router';

import {
  catchError,
  first,
  map,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';

import { AuthService } from '@auth0/auth0-angular';
import { SondermindEvent, SondermindEvents } from '@sondermind/analytics';
import { ConfigurationService } from '@sondermind/configuration';
import { UrlTargetService } from '@sondermind/data-access/auth-utils';
import { Logger } from '@sondermind/datadog-logging';
import { SegmentService } from '@sondermind/segment';
import { Auth0TokenService } from '../services/auth0.service';
import { AuthTokenService } from '../services/auth_token.service';
import { UNAUTHORIZED_REDIRECT_ROUTE } from '../tokens/unauthorized-redirect-route.token';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private authTokenService: AuthTokenService,
    private configurationService: ConfigurationService,
    private router: Router,
    private auth0Service: Auth0TokenService,
    @Optional()
    @Inject(UNAUTHORIZED_REDIRECT_ROUTE)
    private unauthorizedRedirectRoute: string | null,
    private auth0: AuthService,
    private urlTargetService: UrlTargetService,
    private logger: Logger,
    private segmentService: SegmentService<SondermindEvents>,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return this.interceptCore(req, next, true);
  }

  private interceptCore(req: HttpRequest<any>, next: HttpHandler, retryOnFailure: boolean): Observable<HttpEvent<any>> {
    // bail if we're not requesting something against our api
    if (!this.urlTargetService.targetsSonderMindApis(req)) { return next.handle(req) }

    // use admin access token and provider id header when in admin access mode
    if (this.authTokenService.isAdminAccessMode()) {
      // an explicit Authorization header means this IS the refresh call itself — pass through
      if (req.headers.has('Authorization')) {
        return next.handle(req);
      }

      // if the admin session is terminal, short-circuit and error
      if (this.authTokenService.isAdminAccessExpired) {
        return throwError(() => new Error('admin_access_session_expired'));
      }

      const attach = (token: string): Observable<HttpEvent<any>> => {
        const adminReq = req.clone({
          setHeaders: {
            Authorization: `Bearer ${token}`,
            'X-Provider-Id': this.authTokenService.getAdminProviderId(),
          }
        });
        return next.handle(adminReq);
      };

      const adminRefreshMarginMs = 180 * 1000;
      if (this.authTokenService.isAdminAccessTokenExpiringWithin(adminRefreshMarginMs)) {
        return this.authTokenService.refreshAdminAccessToken().pipe(
          switchMap((token) => attach(token))
        );
      }
      return attach(this.authTokenService.getAdminAccessToken());
    }

    // bail if skipAuthorization was used (Authorization: 'None' header)
    // or if the request already has a valid authorization header during OAuth flow
    const authHeader = req.headers.get('Authorization');
    if (authHeader === 'None') {
      return next.handle(req).pipe(
        catchError((e) => this.errorHandler(req, next, e, retryOnFailure))
      );
    }
    if (req.headers.has('Authorization') && !this.auth0Service.auth0Enabled) {
      return next.handle(req).pipe(
        catchError((e) => this.errorHandler(req, next, e, retryOnFailure))
      );
    }

    const origReq = req.clone();
    return this.auth0Service.auth0Enabled$.pipe(
      take(1),
      switchMap((enabled) => iif(
        () => enabled,
        defer(() => this.auth0.isAuthenticated$.pipe(
          take(1),
          switchMap((isAuthenticated) => {
            if (!isAuthenticated) {
              // Not authenticated - don't try to get token, proceed without auth
              this.logger.debug('Not authenticated, proceeding without auth');
              return of('');
            }
            // Use barrier method to get token - prevents concurrent refresh token exchanges
            return this.auth0Service.getTokenWithBarrier().pipe(
              catchError((err) => {
                const errorMessage = err instanceof Error ? err.message : String(err);
                // During initialization or when refresh token is missing/invalid,
                // Auth0 may not be able to refresh. Proceed without auth instead of failing immediately.
                // The request will likely fail with 401, which triggers a retry with forceRefresh.
                this.logger.debug('Failed to get Auth0 token, proceeding without auth', { error: errorMessage });
                return of('');
              })
            );
          })
        )),
        this.resolveOAuthToken()
      )),
      switchMap((token) => {
        const reqWithAuth = token
          ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
          : req;
        return next.handle(reqWithAuth);
      }),
      catchError((e) => this.errorHandler(origReq, next, e, retryOnFailure))
    );
  }
  private resolveAuth0Token$(): Observable<string> {
    return this.auth0Service.auth0Token$;
  }

  // if there's no provided token resolver, we do it naively with the oauth service
  private resolveOAuthToken(): Observable<string> {
    // if we have a valid token, use it
    if (this.authTokenService.isAuthenticated()) {
      return of(this.authTokenService.getAuthToken());
    }

    // otherwise do a silent refresh loop
    return from(this.authTokenService.silentRefresh()).pipe(
      map(() => this.authTokenService.getAuthToken())
    );
  }

  // reruns the given request after a delay if the failure was authentication related
  private errorHandler(
    req: HttpRequest<any>,
    next: HttpHandler,
    error: any,
    retry: boolean
  ): Observable<HttpEvent<any>> {
    const httpError = error as HttpErrorResponse;
    // Using feature flag to stop treating 403s as token refresh candidates.
    const isAuthFailure = httpError?.status === 401;

    // Sending 403 errors to amplitude so we can find journeys leading to this error
    if (httpError?.status === 403) {
      this.segmentService.track(
        SondermindEvent.Provider403Error,
        { url: req.url, method: req.method, retry }
      );
    }

    if (isAuthFailure) {
      if (retry) {
        return timer(500).pipe(
          switchMap(() => this.interceptCore(req, next, false))
        );
      }

      if (this.auth0Service.auth0Enabled) {
        this.logger.info('Handling Auth0 authentication failure', { url: req.url });
        return this.auth0.isAuthenticated$.pipe(
          first(),
          switchMap((isAuthenticated) => {
            if (isAuthenticated) {
              this.logger.info('User is authenticated with Auth0, attempting to refresh token', { url: req.url });
              // Use barrier method with forceRefresh to ensure we get a fresh token after 401
              // The barrier prevents race conditions when multiple requests fail simultaneously
              return this.auth0Service.getTokenWithBarrier(true).pipe(
                tap(() => {
                  this.logger.info('Successfully refreshed Auth0 token via barrier');
                }),
                catchError((tokenError) => {
                  this.logger.error('Auth0 token refresh failed, logging out', { error: tokenError.message });
                  this.auth0Service.logout();
                  return throwError(() => new Error('Token refresh failed'));
                }),
                switchMap((token) => {
                  this.logger.debug('Updated Auth0 token and retrying request', { url: req.url });
                  const newReq = req.clone({
                    setHeaders: {
                      Authorization: `Bearer ${token}`
                    }
                  });

                  return next.handle(newReq);
                })
              );
            }
            return this.redirectAndLogout(req, error);
          })
        );
      }
      return this.redirectAndLogout(req, error);
    }

    return throwError(() => error);
  }

  private redirectAndLogout(req: HttpRequest<unknown>, error: unknown): Observable<null> {
    this.logger.debug('Logging out due to 401 or 403', { request: req, error });

    const redirectRoute = this.unauthorizedRedirectRoute
      || `${this.configurationService.env.authBase}/session/logout`;

    return of(this.router.navigateByUrl(redirectRoute)).pipe(
      tap(() => this.authTokenService.logout()),
      switchMap(() => of(null))
    );
  }
}
